/**
 * OpenStreetMap Nominatim search service.
 * Provides a lightweight wrapper around the public API with basic shaping.
 * NOTE: Public Nominatim has strict usage policy (rate limiting, user-agent requirement).
 * Consider moving to a backend proxy if volume increases.
 */
export interface NominatimResult {
  lat: number
  lon: number
  displayName: string
  type: string
  category?: string
  importance?: number
  name?: string
  address?: {
    house_number?: string
    road?: string
    neighbourhood?: string
    suburb?: string
    city?: string
    town?: string
    village?: string
    county?: string
    state?: string
    country?: string
    postcode?: string
  }
}

interface RawNominatimResult {
  lat: string
  lon: string
  display_name: string
  type: string
  class: string
  importance?: number
  name?: string
  address?: {
    house_number?: string
    road?: string
    neighbourhood?: string
    suburb?: string
    city?: string
    town?: string
    village?: string
    county?: string
    state?: string
    country?: string
    postcode?: string
  }
}

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search'

export interface NominatimSearchOptions {
  signal?: AbortSignal
  limit?: number
  viewbox?: [number, number, number, number] // left, top, right, bottom
  bounded?: boolean
  acceptLanguage?: string
}

export const searchNominatim = async (
  query: string,
  opts: NominatimSearchOptions = {},
): Promise<NominatimResult[]> => {
  const trimmed = query.trim()
  if (!trimmed) return []

  const params = new URLSearchParams()
  params.set('q', trimmed)
  params.set('format', 'jsonv2')
  params.set('addressdetails', '1')
  params.set('limit', String(opts.limit ?? 8))
  params.set('dedupe', '1')
  params.set('extratags', '0')

  if (opts.viewbox) {
    params.set('viewbox', opts.viewbox.join(','))
    if (opts.bounded) params.set('bounded', '1')
  }

  const url = `${NOMINATIM_BASE}?${params.toString()}`

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      // Basic UA to comply with Nominatim policy (should be customized further if needed)
      'Accept-Language': opts.acceptLanguage || 'en',
      'User-Agent': 'Routecamp/1.0 (Waypoint Search)',
    },
    signal: opts.signal,
  })

  if (!res.ok) {
    if (res.status === 429) {
      throw new Error('Rate limited by Nominatim. Please wait and try again.')
    }
    throw new Error(`Nominatim request failed (${res.status})`)
  }

  const json = (await res.json()) as RawNominatimResult[]
  return json.map((r) => ({
    lat: Number(r.lat),
    lon: Number(r.lon),
    displayName: r.display_name,
    type: r.type,
    category: r.class,
    importance: r.importance,
    name: r.name,
    address: r.address,
  }))
}

/**
 * Extract location name from Nominatim result
 */
export const getLocationName = (result: NominatimResult): string => {
  if (result.name) {
    return result.name
  }

  // Fallback to extracting name from display name
  const parts = result.displayName.split(',')
  return parts[0]?.trim() || 'Waypoint'
}

/**
 * Format address from Nominatim result
 */
export const formatAddress = (result: NominatimResult): string => {
  if (!result.address) {
    // Fallback to display name without the first part (which is the name)
    const parts = result.displayName.split(',')
    return parts.slice(1).join(',').trim()
  }

  const address = result.address
  const addressParts: string[] = []

  // Add street address
  if (address.house_number && address.road) {
    addressParts.push(`${address.house_number} ${address.road}`)
  } else if (address.road) {
    addressParts.push(address.road)
  }

  // Add neighborhood/suburb
  if (address.neighbourhood) {
    addressParts.push(address.neighbourhood)
  } else if (address.suburb) {
    addressParts.push(address.suburb)
  }

  // Add city/town/village
  if (address.city) {
    addressParts.push(address.city)
  } else if (address.town) {
    addressParts.push(address.town)
  } else if (address.village) {
    addressParts.push(address.village)
  }

  // Add state/county if different from city
  if (address.state && !addressParts.includes(address.state)) {
    addressParts.push(address.state)
  } else if (address.county && !addressParts.includes(address.county)) {
    addressParts.push(address.county)
  }

  // Add country
  if (address.country) {
    addressParts.push(address.country)
  }

  return addressParts.join(', ')
}

/**
 * Simple debounce helper for local component usage (not reactive by itself).
 */
export const debounce = <F extends (...args: unknown[]) => void>(fn: F, delay = 400) => {
  let timer: number | undefined
  return (...args: Parameters<F>) => {
    if (timer !== undefined) window.clearTimeout(timer)
    timer = window.setTimeout(() => {
      fn(...args)
    }, delay)
  }
}
